import type {
  Action,
  Diagram,
  Event,
  ExtSim,
  LogicNode,
  MainItemType,
  State,
  Variable,
} from '../../../types/EMRALD_Model';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ReactFlowProvider } from 'reactflow';
import { EventForm } from '@/components/forms/EventForm/EventForm';
import { EventFormContextProvider } from '@/components/forms/EventForm/EventFormContext';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useWindowContext } from '../../../contexts/WindowContext';
import { EMRALD_SchemaVersion } from '../../../types/ModelUtils';
import {
  GetModelItemsReferencedBy,
  GetModelItemsReferencing,
} from '../../../utils/ModelReferences';
import { EmraldDiagram } from '../../diagrams/EmraldDiagram/EmraldDiagram';
import { LogicNodeTreeDiagram } from '../../diagrams/LogicTreeDiagram/LogicTreeDiagram';
import { ActionForm } from '../../forms/ActionForm/ActionForm';
import { ActionFormContextProvider } from '../../forms/ActionForm/ActionFormContext';
import { DiagramForm } from '../../forms/DiagramForm/DiagramForm';
import { ExtSimForm } from '../../forms/ExtSimForm/ExtSimForm';
import { LogicNodeForm } from '../../forms/LogicNodeForm/LogicNodeForm';
import { LogicNodeFormContextProvider } from '../../forms/LogicNodeForm/LogicNodeFormContext';
import { SearchResultForm } from '../../forms/SearchResultForm/SearchResultForm';
import { StateForm } from '../../forms/StateForm/StateForm';
import { TemplateForm } from '../../forms/TemplateForm/TemplateForm';
import { VariableForm } from '../../forms/VariableForm/VariableForm';
import { VariableFormContextProvider } from '../../forms/VariableForm/VariableFormContext';

// Define your Option and OptionsMapping types
export interface Option {
  label: string;
  action: (content: any, handleDelete?: any) => void | Promise<void>; // Pass context values as parameters
}

type OptionsMapping = Record<string, Option[]>;

export function useOptionsMapping() {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'xl'));
  const { addWindow } = useWindowContext();
  const { showAlert } = useAlertContext();
  const optionsMapping: OptionsMapping = {
    Diagrams: [
      {
        label: 'Open',
        action: (diagram: Diagram) => {
          addWindow(diagram.name, <EmraldDiagram diagram={diagram} />, {
            x: 75,
            y: 25,
            width: isMediumScreen ? 600 : 1000,
            height: isMediumScreen ? 400 : 500,
          });
        },
      },
      {
        label: 'Edit Properties',
        action: (diagram: Diagram) => {
          addWindow(
            `Edit Properties: ${diagram.name}`,
            <DiagramForm diagramData={diagram} />,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          diagram: Diagram,
          handleDelete: (
            itemToDelete: Diagram,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(diagram, 'Diagram');
        },
      },
      {
        label: 'Make Template',
        action: (diagram: Diagram) => {
          try {
            const copiedModel = structuredClone(
              GetModelItemsReferencedBy(diagram.name, 'Diagram', 3),
            );
            addWindow(
              `Create Template`,
              <TemplateForm templatedData={copiedModel} />,
              {
                x: 75,
                y: 25,
                width: isMediumScreen ? 600 : 1000,
                height: isMediumScreen ? 400 : 500,
              },
            );
          } catch (error) {
            console.error(error);
            showAlert(
              'Unable to make template, Please report the issue on the GitHub repo so we can address this problem.',
              'error',
            );
          }
        },
      },
      {
        label: 'Export',
        action: (diagram: Diagram) => {
          try {
            const copiedModel = GetModelItemsReferencedBy(
              diagram.name,
              'Diagram',
              2,
            );
            copiedModel.name = diagram.name;
            copiedModel.emraldVersion = EMRALD_SchemaVersion;

            // Convert JSON data to a string
            const jsonString = JSON.stringify(copiedModel, null, 2);

            // Create a Blob (Binary Large Object) with the JSON string
            const blob = new Blob([jsonString], { type: 'application/json' });

            // Create a URL for the Blob
            const url = URL.createObjectURL(blob);

            // Create an <a> element to trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = `${copiedModel.name ?? 'exported-diagram'}.json`;

            // Trigger a click event on the <a> element to initiate the download
            a.click();

            // Clean up by revoking the URL
            URL.revokeObjectURL(url);
          } catch (error) {
            console.error(error);
            showAlert(
              `Unable to export ${diagram.name}, Please report the issue on the GitHub repo so we can address this problem.`,
              'error',
            );
          }
        },
      },
      {
        label: 'Copy',
        action: async (diagram: Diagram) => {
          try {
            const copiedModel = GetModelItemsReferencedBy(
              diagram.name,
              'Diagram',
              2,
            );
            copiedModel.name = diagram.name;
            copiedModel.emraldVersion = EMRALD_SchemaVersion;
            await navigator.clipboard.writeText(
              JSON.stringify(copiedModel, null, 2),
            );
          } catch (error) {
            console.error('Error occurred:', error);
            showAlert(
              `Unable to copy ${diagram.name}, Please report the issue on the GitHub repo so we can address this problem.`,
              'error',
            );
          }
        },
      },
      {
        label: 'Copy Recursive',
        action: async (diagram: Diagram) => {
          try {
            const copiedModel = GetModelItemsReferencedBy(
              diagram.name,
              'Diagram',
              0,
            );
            copiedModel.name = diagram.name;
            copiedModel.emraldVersion = EMRALD_SchemaVersion;
            await navigator.clipboard.writeText(
              JSON.stringify(copiedModel, null, 2),
            );
          } catch (error) {
            console.error(error);
            showAlert(
              `Unable to copy recursive ${diagram.name}, Please report the issue on the GitHub repo so we can address this problem.`,
              'error',
            );
          }
        },
      },
      {
        label: 'Find References',
        action: (diagram: Diagram) => {
          addWindow(
            `Items Referencing ${diagram.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(diagram.name, 'Diagram', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    'Logic Tree': [
      {
        label: 'Open',
        action: (logicNode: LogicNode) => {
          addWindow(
            logicNode.name,
            <ReactFlowProvider>
              <LogicNodeTreeDiagram logicNode={logicNode} />
            </ReactFlowProvider>,
            {
              x: 75,
              y: 25,
              width: isMediumScreen ? 600 : 1000,
              height: isMediumScreen ? 400 : 500,
            },
          );
        },
      },
      {
        label: 'Edit Properties',
        action: (logicNode: LogicNode) => {
          addWindow(
            `Edit Properties: ${logicNode.name}`,
            <LogicNodeFormContextProvider>
              <LogicNodeForm logicNodeData={logicNode} editing />
            </LogicNodeFormContextProvider>,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          logicNode: LogicNode,
          handleDelete: (
            itemToDelete: LogicNode,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(logicNode, 'LogicNode');
        },
      },
      {
        label: 'Find References',
        action: (tree: LogicNode) => {
          addWindow(
            `Items Referencing ${tree.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(tree.name, 'LogicNode', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    'External Sims': [
      {
        label: 'Edit Properties',
        action: (extSim: ExtSim) => {
          addWindow(
            `Edit Properties: ${extSim.name}`,
            <ExtSimForm ExtSimData={extSim} />,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          extSim: ExtSim,
          handleDelete: (
            itemToDelete: ExtSim,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(extSim, 'ExtSim');
        },
      },
      {
        label: 'Find References',
        action: (ext: ExtSim) => {
          addWindow(
            `Items Referencing ${ext.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(ext.name, 'ExtSim', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    Actions: [
      {
        label: 'Edit Properties',
        action: (action: Action) => {
          addWindow(
            `Edit Properties: ${action.name}`,
            <ActionFormContextProvider>
              <ActionForm actionData={action} />
            </ActionFormContextProvider>,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          action: Action,
          handleDelete: (
            itemToDelete: Action,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(action, 'Action');
        },
      },
      {
        label: 'Find References',
        action: (action: Action) => {
          addWindow(
            `Items Referencing ${action.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(action.name, 'Action', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    Events: [
      {
        label: 'Edit Properties',
        action: (event: Event) => {
          addWindow(
            `Edit Properties: ${event.name}`,
            <EventFormContextProvider>
              <EventForm eventData={event} />
            </EventFormContextProvider>,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          event: Event,
          handleDelete: (
            itemToDelete: Event,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(event, 'Event');
        },
      },
      {
        label: 'Find References',
        action: (event: Event) => {
          addWindow(
            `Items Referencing ${event.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(event.name, 'Event', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    States: [
      {
        label: 'Edit Properties',
        action: (state: State) => {
          addWindow(
            `Edit Properties: ${state.name}`,
            <StateForm stateData={state} />,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          state: State,
          handleDelete: (
            itemToDelete: State,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(state, 'State');
        },
      },
      {
        label: 'Find References',
        action: (state: State) => {
          addWindow(
            `Items Referencing ${state.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(state.name, 'State', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
    Variables: [
      {
        label: 'Edit Properties',
        action: (variable: Variable) => {
          addWindow(
            `Edit Properties: ${variable.name}`,
            <VariableFormContextProvider>
              <VariableForm variableData={variable} />
            </VariableFormContextProvider>,
          );
        },
      },
      {
        label: 'Delete',
        action: (
          variable: Variable,
          handleDelete: (
            itemToDelete: Variable,
            itemToDeleteType: MainItemType,
          ) => void,
        ) => {
          handleDelete(variable, 'Variable');
        },
      },
      {
        label: 'Find References',
        action: (v: Variable) => {
          addWindow(
            `Items Referencing ${v.name}`,
            <SearchResultForm
              model={GetModelItemsReferencing(v.name, 'Variable', 1)}
              getModel={() => <></>}
              expandable={false}
            />,
          );
        },
      },
    ],
  };

  return optionsMapping;
}
